import { t, type PlainKey } from '../i18n/t.ts';
import type { Register } from '../profile/register.ts';
import { ADULT_SCENES, ADULT_TOPICS, KID_GAMES, KID_TOPICS, type Topic } from './topics.english.ts';

/** A name, not a component, so this stays a `.ts` the tests can import; `SessionStart` maps it. */
export type TopicIcon =
  | 'food'
  | 'travel'
  | 'work'
  | 'family'
  | 'weekend'
  | 'presentation'
  | 'animals'
  | 'toys'
  | 'school'
  | 'sports'
  | 'colours'
  | 'space'
  | 'games'
  | 'cartoons'
  | 'music'
  | 'riddles'
  | 'guess'
  | 'teach'
  | 'story'
  | 'spy'
  | 'rather'
  | 'rhyme'
  | 'cafe'
  | 'doctor'
  | 'interview'
  | 'hotel'
  | 'shop'
  | 'directions';

const TOPIC_COPY: Record<Topic, { label: PlainKey; icon: TopicIcon }> = {
  Food: { label: 'topic-food', icon: 'food' },
  Travel: { label: 'topic-travel', icon: 'travel' },
  Work: { label: 'topic-work', icon: 'work' },
  Family: { label: 'topic-family', icon: 'family' },
  'Weekend plans': { label: 'topic-weekend-plans', icon: 'weekend' },
  Presentations: { label: 'topic-presentations', icon: 'presentation' },
  Animals: { label: 'topic-animals', icon: 'animals' },
  Sports: { label: 'topic-sports', icon: 'sports' },
  'Toys and games': { label: 'topic-toys-and-games', icon: 'toys' },
  School: { label: 'topic-school', icon: 'school' },
  Colours: { label: 'topic-colours', icon: 'colours' },
  Space: { label: 'topic-space', icon: 'space' },
  Games: { label: 'topic-games', icon: 'games' },
  Cartoons: { label: 'topic-cartoons', icon: 'cartoons' },
  Music: { label: 'topic-music', icon: 'music' },
  [KID_GAMES[0]]: { label: 'topic-riddles', icon: 'riddles' },
  [KID_GAMES[1]]: { label: 'topic-guess-the-animal', icon: 'guess' },
  // The fallback only: `topicLabel` names the character (kid play design §7.5).
  [KID_GAMES[2]]: { label: 'topic-teach-me', icon: 'teach' },
  [KID_GAMES[3]]: { label: 'topic-story-time', icon: 'story' },
  [KID_GAMES[4]]: { label: 'topic-i-spy', icon: 'spy' },
  [KID_GAMES[5]]: { label: 'topic-would-you-rather', icon: 'rather' },
  [KID_GAMES[6]]: { label: 'topic-rhyme-time', icon: 'rhyme' },
  [ADULT_SCENES[0]]: { label: 'topic-scene-cafe', icon: 'cafe' },
  [ADULT_SCENES[1]]: { label: 'topic-scene-doctor', icon: 'doctor' },
  [ADULT_SCENES[2]]: { label: 'topic-scene-interview', icon: 'interview' },
  [ADULT_SCENES[3]]: { label: 'topic-scene-hotel', icon: 'hotel' },
  [ADULT_SCENES[4]]: { label: 'topic-scene-return', icon: 'shop' },
  [ADULT_SCENES[5]]: { label: 'topic-scene-directions', icon: 'directions' },
};

const SCENE_GOALS: Record<(typeof ADULT_SCENES)[number], PlainKey> = {
  [ADULT_SCENES[0]]: 'scene-goal-cafe',
  [ADULT_SCENES[1]]: 'scene-goal-doctor',
  [ADULT_SCENES[2]]: 'scene-goal-interview',
  [ADULT_SCENES[3]]: 'scene-goal-hotel',
  [ADULT_SCENES[4]]: 'scene-goal-return',
  [ADULT_SCENES[5]]: 'scene-goal-directions',
};

const SCENE_PARTS: Record<(typeof ADULT_SCENES)[number], readonly [PlainKey, PlainKey, PlainKey]> = {
  [ADULT_SCENES[0]]: ['scene-part-cafe-drink', 'scene-part-cafe-food', 'scene-part-cafe-pay'],
  [ADULT_SCENES[1]]: ['scene-part-doctor-wrong', 'scene-part-doctor-when', 'scene-part-doctor-answer'],
  [ADULT_SCENES[2]]: ['scene-part-interview-intro', 'scene-part-interview-example', 'scene-part-interview-ask'],
  [ADULT_SCENES[3]]: ['scene-part-hotel-name', 'scene-part-hotel-nights', 'scene-part-hotel-ask'],
  [ADULT_SCENES[4]]: ['scene-part-return-bought', 'scene-part-return-wrong', 'scene-part-return-want'],
  [ADULT_SCENES[5]]: ['scene-part-directions-ask', 'scene-part-directions-detail', 'scene-part-directions-repeat'],
};

/**
 * A scene's goal parts in the display language, in the order of
 * `tutor_core::scene::goal_parts`, which the debrief's `goal.parts` follows;
 * null for any other topic.
 */
export function sceneParts(topic: string): string[] | null {
  return Object.hasOwn(SCENE_PARTS, topic)
    ? SCENE_PARTS[topic as (typeof ADULT_SCENES)[number]].map((key) => t(key))
    : null;
}

/** What a role-play scene asks of the learner, in the display language; null for any other topic. */
export function sceneGoal(topic: string): string | null {
  return Object.hasOwn(SCENE_GOALS, topic) ? t(SCENE_GOALS[topic as (typeof ADULT_SCENES)[number]]) : null;
}

/**
 * The games row, with Story time first from six in the evening (kid
 * engagement PRD, F1): most of the one real child's sessions start after
 * seven, and Story time is the game meant for then. Otherwise as listed.
 */
export function orderGames<T extends string>(games: readonly T[], localHour: number): T[] {
  if (localHour < 18) return [...games];
  const story = games.filter((g) => g === KID_GAMES[3]);
  return [...story, ...games.filter((g) => g !== KID_GAMES[3])];
}

export function topicsFor(register: Register): readonly Topic[] {
  return register === 'kid' ? KID_TOPICS : ADULT_TOPICS;
}

export function topicIcon(topic: Topic): TopicIcon {
  return TOPIC_COPY[topic].icon;
}

/**
 * A stored topic's label; one this build does not curate shows as stored.
 * Both registers' lists, because a session keeps its topic after the learner
 * changes tutor.
 *
 * `name` is the character the label speaks of: the teach game is "Teach
 * Cappy" on screen though its value says "you", so one value serves every
 * character (kid play design §7.5). With no character known it asks to
 * teach "me" rather than leave a hole where the name goes.
 */
export function topicLabel(topic: string, name?: string | null): string {
  if (topic === KID_GAMES[2] && name) return t('topic-teach', { name });
  return Object.hasOwn(TOPIC_COPY, topic) ? t(TOPIC_COPY[topic as Topic].label) : topic;
}

export type SessionInput = {
  learnerId: string;
  mode: 'FREE_CHAT' | 'TOPIC';
  topic?: string;
};

/**
 * Extracted from `SessionView.begin` so the mode/topic pairing — FREE_CHAT
 * never carries a topic, TOPIC never lacks one — is a test rather than
 * something a reviewer has to verify by reading the dispatch.
 */
export function sessionInput(learnerId: string, topic?: string): SessionInput {
  return topic ? { learnerId, mode: 'TOPIC', topic } : { learnerId, mode: 'FREE_CHAT' };
}
